import type { TextKey } from '../../i18n/i18n';
import { t } from '../../i18n/i18n';
import { el } from './dom';

export interface Device {
  readonly userAgent: string;
  /** How many fingers the screen can feel at once: none on a computer. */
  readonly touchPoints: number;
  /** Whether the game was opened from the home screen, without the browser around it. */
  readonly standalone: boolean;
}

/** How to put the game on the home screen, where it fills the whole screen; nothing on a computer or once it is there. */
export function homeScreenSteps(device: Device): TextKey | null {
  if (device.standalone || device.touchPoints === 0) return null;
  return /iPhone|iPad|iPod|Macintosh/.test(device.userAgent) ? 'home-screen-ios' : 'home-screen-android';
}

/** The steps for this device, if it needs them. */
export function homeScreenHint(): string | null {
  const standalone = matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches || (navigator as { standalone?: boolean }).standalone === true;
  const steps = homeScreenSteps({ userAgent: navigator.userAgent, touchPoints: navigator.maxTouchPoints, standalone });
  return steps && t(steps);
}

/**
 * The fight is made for a wide screen. A phone held upright is asked to turn, and everything waits under this until
 * it does: phones do not let a web page lock the screen's turn.
 */
export class TurnPhone {
  private readonly upright = matchMedia('(orientation: portrait) and (pointer: coarse)');
  private readonly cover: HTMLElement;

  constructor(root: HTMLElement) {
    this.cover = el('div', 'turn-phone', root);
    el('div', 'turn-phone-icon', this.cover);
    el('div', 'turn-phone-text', this.cover, t('turn-phone'));
    const hint = homeScreenHint();
    if (hint) el('div', 'turn-phone-hint', this.cover, hint);
    this.upright.addEventListener('change', () => this.update());
    this.update();
  }

  /** Whether the phone is held upright, and the game waits. */
  get waiting(): boolean {
    return this.upright.matches;
  }

  private update(): void {
    this.cover.classList.toggle('shown', this.upright.matches);
  }
}
